import { motion, useReducedMotion } from "motion/react";

const EASE = [0.22, 1, 0.36, 1];

/** Scroll-triggered rise + fade. Falls back to a plain fade when motion is reduced. */
export function Reveal({ children, delay = 0, y = 24, className = "", as = "div", ...rest }) {
  const reduce = useReducedMotion();
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={reduce ? { duration: 0.01 } : { duration: 0.5, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Editorial blur-fade used for section introductions. */
export function BlurFade({ children, delay = 0, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, filter: "blur(8px)", y: 14 }}
      whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={reduce ? { duration: 0.01 } : { duration: 0.55, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/** Hero entrance: immediate (not scroll bound) so above-the-fold content never waits. */
export function HeroReveal({ children, delay = 0, className = "", ...rest }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0.01 } : { duration: 0.55, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Card wrapper with hover lift. Lift is dropped entirely under reduced motion. */
export function LiftCard({ children, className = "", delay = 0, ...rest }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      whileHover={reduce ? undefined : { y: -6 }}
      transition={reduce ? { duration: 0.01 } : { duration: 0.45, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Gentle float for the official award asset. Static when motion is reduced. */
export function FloatIn({ children, className = "" }) {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, scale: 0.94 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
